<template>
  <div
    class="select"
    :style="{ width }"
    @focusout="blurHandler"
    tabindex="-1"
    ref="select"
  >
    <label
      @click="toggleDialog"
      class="input__label"
      v-if="label && readonly && !hidden"
    >
      {{ label }}
    </label>

    <!-- readonly -->
    <div
      v-if="readonly"
      style="display: flex; font-size: 16px; margin-top: 4px"
    >
      <span v-if="shownSelected === placeholder"> - </span>

      <slot v-else-if="!multiple" name="item" :item="shownSelected">
        {{ shownSelected }}
      </slot>

      <span v-else>
        <slot v-for="item in inputValue" name="item" :item="item">
          {{ item }}
        </slot>
      </span>
    </div>

    <!-- selectable -->
    <div class="selectable-content" v-else>
      <!-- field -->
      <div
        :class="['select-field', { disabled, error: hasValidationError }, { 'select-status': statusTicket }]"
        @click.stop="toggleDialog"
        :style="styleSelected"
        style="padding: 0 13px;"
      >
        <div v-if="shownSelected" class="select__shown-selected">
          <div
            v-if="search && (selectDialog || shownSelected === placeholder)"
            style="display: flex; align-items: center"
          >
            <svgicon
                v-if="!withButton"
                color="#b8cad5"
                icon="search"
                width="20px"
                style="margin-right: 9px; min-width: 20px"
            />

            <input
              :placeholder="placeholder"
              v-model="searchValue"
              ref="input"
              @keyup.enter="loadSearch"
            />
          </div>

          <slot
            v-else-if="shownSelected !== placeholder"
            name="item"
            :item="shownSelected"
          >
            <div v-if="department"
              style="width: fit-content"
              class="department-select"
            >
              <div v-if="shownSelected.parent">
                <p v-if="shownSelected.parent.parent" class="parent-department">
                  {{shownSelected.parent.parent.name}}
                </p>
                <p class="parent-department">
                  /{{shownSelected.parent.name}}/
                </p>
              </div>
              {{ shownSelected.name }}
            </div>
            <span v-else>{{ shownSelected }}</span>
          </slot>

          <span v-else>
            {{ placeholder }}
          </span>
        </div>

        <span v-if="multiple && !onlyPlaceholder" class="multiple" :class="department ? 'department' : '' || status? 'status' : ''">
          <!-- <transition-group name="fade"> -->
          <span
            v-for="(item, index) in inputValue"
            :key="index"
            :class="[
              'selected__item',
              { item__background: selectedWithBackground },
            ]"
          >
            <span :style="department ? 'width: 400px' : 'width: fit-content'">
              <slot name="item" :item="itemText ? item[itemText] : item">
              {{ itemText ? item[itemText] : item}}</slot>
            </span>
            <div class="multi__clear-icon" @click.stop="deleteItem(item)">
              <svgicon icon="clear" color="#536D86" width="15px" />
            </div>
          </span>
          <!-- </transition-group> -->
        </span>

        <span class="icons__control">
          <svgicon
            v-if="(!multiple && shownSelected !== placeholder && !disabled) && clearInputBtn"
            icon="clear"
            width="15px"
            class="clear-icon"
            @click.stop="clearValue"
          />

          <svgicon
            v-if="!search && !disabled"
            icon="arrow"
            width="15px"
            :class="['dropdown-icon', { active: selectDialog }]"
            style="margin-left: 10px"
          />
        </span>
      </div>

      <!-- error message -->
      <slot v-if="hasValidationError" />

      <!-- dialog -->
      <transition :duration="200" name="select">
        <span
          v-if="!searchSelect && !isLoadingSearch"
          v-show="selectDialog"
          ref="selectDialog"
          :class="['selectDialog', { before: !insideWindow }, { 'dialog-status': statusTicket }]"
        >
          <span
            v-for="(item, index) in shownItems"
            :key="index"
            @click.stop="changeSelected(item)"
            :class="['item', { selected: isSelected(item) }, status? 'item--status' : '']"
            :style="{ height: `${height}px` }"
          >
            <Checkbox v-if="withCheckbox" :value="isSelected(item)" />

            <slot name="item" :item="item">
              <span style="width: fit-content">
                {{ itemText ? item[itemText] : item }}
              </span>
            </slot>
          </span>

          <div
            v-if="!shownItems || !shownItems.length"
            class="item"
            style="pointer-events: none; margin: 0 auto"
          >
            нет вариантов
          </div>
        </span>

        <span v-if="searchSelect && selectDialog" class="searchSelect">
          <VInput
            v-model="searchValue"
            icon="search"
            placeholder="Найти в списке"
            style="height: 40px; margin-bottom: 30px"
            @enter="filterItems"
          />

          <div class="user__list">
            <span
              v-for="(item, index) in shownItems"
              :key="index"
              @click.stop="setUser(item, index)"
              :class="[
                'item',
                { selected: !withCheckbox && searchSelected === item },
              ]"
              :style="{ height: `${height}px` }"
            >
              <Checkbox
                v-if="withCheckbox"
                :value="
                  multiple
                    ? checkMultiple(item)
                    : checkSingle(item)
                "
                style="margin-right: 14px"
              />

              <slot name="item" :item="item">
                <div v-if="department"
                  style="width: fit-content"
                >
                  <div v-if="item.parent" class="parent-department">
                    <p v-if="item.parent.parent" class="parent-department">
                      {{item.parent.parent.name}}
                    </p>
                    <p class="parent-department">
                      /{{item.parent.name}}/
                    </p>
                  </div>
                  {{ item.name }}
                </div>
                <span style="width: fit-content" v-else>
                  {{ itemText ? item[itemText] : item }} 
                </span>
              </slot>
            </span>
          </div>

          <div class="buttons">
            <Button
              width="100%"
              height="40px"
              text="Отмена"
              class="btn-secondary"
              @click.native.prevent="
                (searchSelected = null), (selectDialog = false)
              "
            />
            <Button
              width="100%"
              height="40px"
              text="Применить"
              class="btn-primary"
              @click.native.prevent="
                multiple
                  ? ((inputValue = searchSelected), (selectDialog = false))
                  : changeSelected(searchSelected)
              "
            />
          </div>
        </span>
      </transition>

      <!-- button -->
      <Button
          v-if="withButton"
          type="button"
          class="btn-sm btn-primary btn-primary--search "
          is-sm
          icon="search"
          :loading="isLoadingSearch"
          width="20"
          height="20"
          icon-width="20"
          @click.native="loadSearch"
          @focusout.native.stop
      />
    </div>
  </div>
</template>

<script>
import Base from "@/components/Form/Vinput/Base/Base.vue";
import Vue from "vue";
import Checkbox from "@/components/Form/Checkbox.vue";
import VInput from "@/components/Form/Vinput/Vinput.vue";
import Button from "@/components/Button.vue";
import { getDepartmentsSearch } from "@/api/departments";
import { getFiliationSearch } from "@/api/filiations";
import {
  getPassengerWithoutDepartamentSearch,
  getUserFromDepartamentSearch,
  getUsersSearch,
  getUsersSearchWithRole
} from "@/api/users";
import { getPassengersSearch } from "@/api/passengers";
// import {getAllTicketsInfo} from "@/api/files";

export default {
  name: "Select",
  components: { Checkbox, VInput, Button },
  inject: { formData: { from: "formData", default: null } },
  extends: Base,
  props: {
    clearInputBtn: {
      type: Boolean,
      default: true,
    },
    value: {
      type: [Object, Array],
      default: null,
    },
    width: {
      type: String,
      default: "100%",
    },
    height: {
      type: String,
      default: "100%",
    },
    itemText: {
      type: String,
      default: null,
    },
    items: {
      type: Array,
      default: null,
    },
    search: {
      type: [Boolean, String],
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    department: {
      type: Boolean,
      default: false,
    },
    filiation: {
      type: Boolean,
      default: false,
    },
    users: {
      type: Boolean,
      default: false,
    },
    passengers: {
      type: Boolean,
      default: false,
    },
    logist: {
      type: Boolean,
      default: false,
    },
    leader: {
      type: Boolean,
      default: false,
    },
    status: {
      type: Boolean,
      default: false,
    },
    selectedWithBackground: {
      type: Boolean,
      default: false,
    },
    resetIfSame: {
      type: Boolean,
      default: false,
    },
    sort: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    withCheckbox: {
      type: Boolean,
      default: false,
    },
    onlyPlaceholder: {
      type: Boolean,
      default: false,
    },
    searchSelect: {
      type: Boolean,
      default: false,
    },
    statusTicket: {
      type: Boolean,
      default: false,
    },
    isGtt: {
      type: Boolean,
      required: false
    },
    isNoGtt: {
      type: Boolean,
      required: false
    },
    isAutoSearch: {
      type: Boolean,
      required: false,
      default: true
    },
    withButton: {
      type: Boolean,
      required: false,
      default: false
    },
    hidden: {
      type: Boolean,
      default: false
    },
    multiModels: {
      type: Boolean,
      default: false
    },
    tickets: {
      type: Boolean,
      default: false,
    },
  },
  model: {
    prop: "value",
    event: "change",
  },
  data() {
    return {
      defaultValue: this.multiple ? [] : this.value,
      selectDialog: false,
      searchValue: "",
      shownItems: this.items,
      searchSelected: null,
      insideWindow: true,
      isLoadingSearch: false
    };
  },
  mounted() {
    this.searchSelected = this.value
  },
  updated() {
    if (!this.searchValue) {
      this.shownItems = this.items;
    }
  },
  computed: {
    inputValue: {
      get() {
        if (this.formData && this.name) {
          return this.formData[this.name] || this.defaultValue;
        } else {
          return this.value || this.defaultValue;
        }
      },
      set(val) {
        if (this.formData && this.name) {
          Vue.set(this.formData, this.name, val);
        }
        this.$emit("change", val);
      },
    },
    shownSelected() {
      if (this.multiple) {
        return this.inputValue.length && !this.onlyPlaceholder
          ? null
          : this.placeholder;
      }

      if (!this.inputValue || !this.hasSelected) {
        return this.placeholder;
      }

      if (this.itemText && !this.department) {
        return this.inputValue[this.itemText];
      }

      if(this.department) {
        return this.inputValue
      }

      return this.inputValue;
    },
    styleSelect() {
      return {
        width: this.width,
        height: this.height,
      };
    },
    hasSelected() {
      if (this.inputValue instanceof Object) {
        return Object.keys(this.inputValue).length;
      } else if (this.inputValue instanceof Array) {
        return this.inputValue.length;
      } else {
        return !!this.inputValue;
      }
    },
    styleSelected() {
      return {
        color: !this.hasSelected || this.disabled ? "#B8CAD5" : null,
        width: this.width,
        height: this.height,
      };
    },
    rules() {
      const validators = {};

      if (this.required) {
        if (!this.inputValue) {
          validators.required = () => false;
        }

        if (this.inputValue && this.inputValue instanceof Object) {
          validators.required = (val) => val && Object.keys(val).length;
        }
        if (this.inputValue && this.inputValue instanceof Array) {
          validators.required = (val) => val && val.length;
        }
      }

      return validators;
    },
    getSearchField() {
      let searchField

      if (this.searchSelect) {
        searchField = 'FIO'

        if (!this.user || !this.logist) {
          searchField = 'name'
        }

        if (this.tickets) {
          searchField = 'number_ticket'
        }
      } else if (typeof this.search === "string" && this.search) {
        searchField = this.search

      } else if (this.itemText) {
        searchField = this.itemText
      }

      return searchField
    }
  },
  getSelected() {
    return this.searchSelected
  },
  methods: {
    toggleDialog() {
      if (this.value) {
        this.searchSelected = this.value
      }
      if (!this.loading && !this.readonly && !this.disabled) {
        if (this.search) {
          if (!this.selectDialog) {
            this.selectDialog = true;
          }

          this.$nextTick(() => {
            this.$refs.input?.focus();
          });
        } else {
          this.selectDialog = !this.selectDialog;
        }
      }
    },
    isSelected(item) {
      // Отрабатывает инпут
      if (!this.inputValue) {
        return false;
      }

      if (Array.isArray(this.inputValue)) {
        if(this.multiModels) {
          return this.inputValue.some(el => el.id === item.id && el.model === item.model);
        } else {
          return this.inputValue.some(el => el.id === item.id);
        }
      } else {
        return this.inputValue === item;
      }
    },
    changeSelected(item) {
      if (this.multiple) {
        let copy = this.inputValue;

        if (this.multiModels) {
          const delEl = copy.find(el => el.id === item.id && el.model === item.model)
          if (delEl) {
            copy = copy.filter(el => (el !== delEl));
          } else {
            copy = [...copy, item];
          }
        } else {
          if (copy.find(el => el.id === item.id)) {
            copy = copy.filter(el => el.id !== item.id);
          } else {
            copy = [...copy, item];
          }
        }

        if (this.inputValue !== copy) {
          this.inputValue = copy;
        }
      } else {
        if (this.inputValue !== item) {
          this.inputValue = item;
        } else if (this.resetIfSame) {
          this.inputValue = {};
        }
      }
      if (!this.withCheckbox) this.selectDialog = false;
    },
    async loadSearch() {
      if (!this.searchValue) return

      try {
        this.isLoadingSearch = true
        const response = await this.switchRequest(this.searchValue)
        // Поддержка обоих форматов ответа: axios response и уже распакованный res.data
        this.shownItems = (response && response.data !== undefined)
          ? (response.data || null)
          : (response || null)

      } finally {
        this.isLoadingSearch = false
      }
    },

    // TODO: разделить асинхронный поиск и обычный фильтр
    filterItems() {
      if (this.searchValue === "") {
        this.shownItems = this.items; // this.sortedItems;
      } else {
        if (this.searchSelect) {
          if (this.users) {
              getUsersSearch(this.searchValue).then(
                (res) => (this.shownItems = res.data || null)
              )
          } else if (this.logist) {
            const role = 5
            getUsersSearchWithRole(this.searchValue, role).then(
                (res) => (this.shownItems = res.data || null)
            )
          } else if (this.leader) {
            const role = 3
            getUsersSearchWithRole(this.searchValue, role).then(
                (res) => (this.shownItems = res.data || null)
            )
          } else if (this.passengers) {
            const role = 3
            getPassengersSearch(this.searchValue, role).then(
                (res) => (this.shownItems = res.data || null)
            )
          } else {
            if(this.department) {
              getDepartmentsSearch(this.searchValue).then(
                  (res) => (this.shownItems = res.data || null)
              );
            } else if(this.filiation) {
              getFiliationSearch(this.searchValue).then(
                  (res) => (this.shownItems = res.data || null)
              );
            }
          }
        } else if (typeof this.search === "string" && this.search) {
          if (this.isGtt) {
            getUserFromDepartamentSearch(this.searchValue).then(
              (res) => (this.shownItems = res.data || null)
            )
          } else if (this.isNoGtt) {
            getPassengerWithoutDepartamentSearch(this.searchValue).then(
              (res) => (this.shownItems = res.data || null)
            )
          } else {
            getUsersSearch(this.searchValue).then(
              (res) => (this.shownItems = res.data || null)
            )
          }
        }

        this.shownItems = this.items.filter((item) => {
          const value = this.getSearchField ? item[this.getSearchField] : item;
          if (typeof value !== 'object') {
            return value?.toLowerCase().includes(this.searchValue?.toLowerCase());
          } else {
            return false
          }
        });
      }
    },
    switchRequest(value) {
      if (this.users) {
        return getUsersSearch(value)
      }

      if (this.logist) {
        return getUsersSearchWithRole(value, 5)
      }

      if (this.department) {
        return getDepartmentsSearch(value)
      }

      if (this.filiation) {
        return getFiliationSearch(value)
      }

      if (this.isGtt) {
        return getUserFromDepartamentSearch(value)
      }

      if (this.isNoGtt) {
        return getPassengerWithoutDepartamentSearch(value)
      }

      if (this.passengers) {
        return getPassengersSearch(value)
      }

      return getUsersSearch(value)
    },
    sortedItems() {
      let items = this.items;
      if (this.sort) {
        if (this.itemText) {
          return items.sort((a, b) => a[this.itemText] > b[this.itemText]);
        } else {
          return items.sort();
        }
      } else {
        return items;
      }
    },
    deleteItem(item) {
      if (this.searchSelected?.length) {
        this.searchSelected = this.searchSelected.filter(el => el.id !== item.id)
      }
      if (this.multiModels) {
          const delEl = this.inputValue.find(el => el.id === item.id && el.model === item.model)
          this.inputValue = this.inputValue.filter(el => el !== delEl)
      } else this.inputValue = this.inputValue.filter(el => el.id !== item.id)
    },
    blurHandler(event) {
      const enteringParent = this.$refs.select.contains(event.relatedTarget);

      if (!enteringParent) {
        this.selectDialog = false;

        if (this.search) this.searchValue = "";
      }
    },
    setUser(item) {
      if (this.multiple) {
        if (this.searchSelected && this.searchSelected.some((us) => us.id === item.id)) {
          this.searchSelected = this.searchSelected.filter(el => el.id !== item.id)
          // this.$delete(this.searchSelected, index);
        } else if (this.searchSelected instanceof Array) {
          this.searchSelected = [...this.searchSelected, item];
        } else this.searchSelected = [item];
      } else {
        this.searchSelected === item
          ? (this.searchSelected = null)
          : (this.searchSelected = item);
      }
    },
    clearValue() {
      this.searchSelected = null
      this.value = null
      this.inputValue = null
      this.defaultValue = null
    },
    checkMultiple(item) {
      return this.searchSelected && this.searchSelected.some((us) => us.id == item.id)
    },
    checkSingle(item) {
      return this.searchSelected === item
    }
  },
  watch: {
    selectDialog(val) {
      if (!val && this.searchSelect) {
        this.searchSelected = null;
      }
    }
  },
};
</script>

<style scoped lang="scss">
.select-enter,
.select-leave-to {
  transform: translateY(5px);
  opacity: 0;
}

.select-enter-active,
.select-leave-active {
  transition: all 0.2s;
}

.select-enter-to,
.select-leave {
  transform: translateY(0);
  opacity: 1;
}

.select {
  position: relative;
  outline: none;
  text-align: left;

  .validation-error {
    color: $red;
    font-size: 12px;
    position: absolute;
  }

  .dropdown-icon {
    fill: $dark-blue;
    transition: transform 0.2s;

    &.active {
      transform: rotate(180deg);
    }
  }

  .clear-icon {
    fill: #536d86;
    transition: 0.2s;

    &:hover {
      fill: $red;
    }
  }

  &__shown-selected {
    // padding-right: 15px;
    width: 100%;
  }
}

.select-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  // max-height: 142px;
  max-width: 100%;
  font-size: $size_l;
  transition: all 0.2s;

  border: 1px solid $gray;
  background: white;
  border-radius: 4px;
  padding: 13px;
  outline: none;
  box-sizing: border-box;
  cursor: default;

  &:not(.disabled) {
    cursor: pointer;
  }

  .multiple {
    display: flex;
    flex-shrink: 1;
    flex-wrap: wrap;
    overflow: auto;
    max-height: 142px;

    &::-webkit-scrollbar {
      width: 8px;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 66, 105, 0.06);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 66, 105, 0.24);
      border-radius: 4px;
    }

    .selected__item {
      display: flex;
      align-items: center;
      margin: 2px 8px 2px 0;
      width: 100%;

      &.item__background {
        background: $light-gray;
        border-radius: 99px;
        padding-right: 8px;
      }
    }

    &.department {
      flex-wrap: nowrap;
      flex-direction: column;
      margin-right: 0;
    }

    &.status {
      max-height: 48px;

      .selected__item {
        width: auto;
      }

      @media screen and (max-width:1500px) {
        max-height: 40px;
      }
    }
  }

  input::placeholder {
    color: #b8cad5;
    font-size: 16px;
  }

  &.disabled {
    background: $light-gray;
    border: none;
  }

  &.error {
    border-color: red;
  }

  @media screen and (max-width:1500px) {
    font-size: 16px;
    min-height: 40px;
    padding: 8px 13px;
  }
}

.selectDialog {
  display: flex;
  flex-direction: column;
  position: absolute;
  background: white;
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.2), 0px 2px 6px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.04);
  box-sizing: border-box;
  max-height: 264px;
  overflow: auto;
  width: 100%;
  z-index: 10;
  animation: showing 0.2s linear;
  border-radius: 4px;

  &:not(.before) {
    top: 100%;
  }

  &.before {
    bottom: 100%;
  }

  @keyframes showing {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .item {
    display: flex;
    align-items: center;
    //justify-content: space-between;
    //min-height: 60px;
    cursor: pointer;
    border-top: 1px solid rgba(83, 109, 134, 0.1);
    padding: 12px;

    &--status {
      display: flex;
      align-items: center;
    }

    &:hover {
      background: $light-gray;
      color: $blue;
    }

    &.selected {
      color: $blue;
      background: $light-gray;
    }
  }

  div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 20px;
  }

  &::-webkit-scrollbar {
    width: 8px;
    background-color: #ebeeed;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 66, 105, 0.24);
    border-radius: 4px;
  }
}

.searchSelect {
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 100%;
  background: white;
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.2), 0px 2px 6px rgba(0, 0, 0, 0.04),
    0px 0px 1px rgba(0, 0, 0, 0.04);
  width: 100%;
  z-index: 10;
  border-radius: 4px;
  padding: 16px;

  .buttons {
    display: flex;
    column-gap: 12px;
  }

  .user__list {
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(0, 66, 105, 0.28);
    border-radius: 4px;
    padding: 10px 0;
    margin-bottom: 20px;
    height: 274px;
    overflow: auto;

    .item {
      display: flex;
      align-items: center;
      padding: 15.5px;
      cursor: pointer;

      &:hover {
        background: $light-gray;
      }

      &.selected {
        background: $gray;
      }
    }

    &::-webkit-scrollbar {
      width: 8px;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 66, 105, 0.06);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 66, 105, 0.24);
      border-radius: 4px;
    }
  }
}

.multi__clear-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  min-width: 32px;
  height: 32px;
  margin-left: 4px;
  transition: 0.2s;

  &:hover {
    background: $light-gray;
  }
}

.icons__control {
  display: flex;
  justify-content: flex-end;
}

.select-status {
  border: none;
  background: none;
  justify-content: right;
  height: 24px;
  min-height: 24px;

  .icons__control {
    min-width: 0;
  }
}

.selectDialog.dialog-status {
  margin-top: 8px;
  max-width: 340px;
  right: 0;
}

.selectable-content {
  display: flex;
  gap: 15px;
}

.department-select {
  padding: 16px 0;
  line-height: 100%;
}
.parent-department {
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  color: #97B2C4;
  margin-bottom: 6px;
}
</style>
